import { useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { PauseCircle, PercentCircle, ArrowRight, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * 3-option save modal shown when a paid user clicks "Cancel subscription".
 * Options: (1) pause 30 days, (2) 50% off next month, (3) cancel anyway.
 * Cancel-anyway invokes the parent-provided onCancel.
 */
export function CancelSaveDialog({ open, onOpenChange, onCancel, onSaved, cancelling }) {
  const [busy, setBusy] = useState(null); // 'pause' | 'discount' | 'cancel' | null

  const pause = async () => {
    setBusy("pause");
    try {
      const { data } = await api.post("/subscription/pause");
      toast.success(data.message);
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const discount = async () => {
    setBusy("discount");
    try {
      const { data } = await api.post("/subscription/discount");
      toast.success(data.message);
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="bg-[#131518] border-[#26282C] text-[#F5F3EE] max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-4xl leading-tight">
            Wait — before you go.
          </AlertDialogTitle>
          <AlertDialogDescription className="text-[#A8ADA5] leading-relaxed">
            You're mid-credit-repair. Bureaus can take 30-45 days per letter. Give it one more cycle
            with a lighter lift on your wallet — pick one below and we&apos;ll keep the momentum going.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3 mt-4">
          {/* Pause 30 days */}
          <button
            onClick={pause}
            disabled={busy !== null}
            data-testid="save-offer-pause"
            className="w-full text-left rounded-2xl border border-[#D5FF3F]/25 bg-[#D5FF3F]/6 hover:bg-[#D5FF3F]/12 transition-colors p-5 flex items-start gap-4 disabled:opacity-50"
          >
            <div className="w-10 h-10 rounded-full bg-[#D5FF3F]/20 flex items-center justify-center flex-shrink-0">
              <PauseCircle size={20} className="text-[#D5FF3F]" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#D5FF3F] mb-1">
                Recommended · zero charge
              </div>
              <div className="font-medium text-[#F5F3EE] mb-1">Pause for 30 days</div>
              <div className="text-sm text-[#A8ADA5]">
                Skip one billing cycle. You still have full access through today's paid period.
                We&apos;ll auto-resume in 30 days unless you cancel.
              </div>
            </div>
            <ArrowRight size={16} className="text-[#D5FF3F] flex-shrink-0 mt-2" />
          </button>

          {/* 50% off next month */}
          <button
            onClick={discount}
            disabled={busy !== null}
            data-testid="save-offer-discount"
            className="w-full text-left rounded-2xl border border-[#7CFF9B]/25 bg-[#7CFF9B]/6 hover:bg-[#7CFF9B]/12 transition-colors p-5 flex items-start gap-4 disabled:opacity-50"
          >
            <div className="w-10 h-10 rounded-full bg-[#7CFF9B]/20 flex items-center justify-center flex-shrink-0">
              <PercentCircle size={20} className="text-[#7CFF9B]" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#7CFF9B] mb-1">
                50% off · next month only
              </div>
              <div className="font-medium text-[#F5F3EE] mb-1">Half price for next month</div>
              <div className="text-sm text-[#A8ADA5]">
                Your next bill will be half — one coupon per subscription. Full price resumes the
                following month unless you cancel.
              </div>
            </div>
            <ArrowRight size={16} className="text-[#7CFF9B] flex-shrink-0 mt-2" />
          </button>

          {/* Cancel anyway */}
          <button
            onClick={() => {
              setBusy("cancel");
              onCancel?.();
            }}
            disabled={busy !== null || cancelling}
            data-testid="save-offer-cancel-anyway"
            className="w-full text-left rounded-2xl border border-[#26282C] bg-transparent hover:bg-[#131518] transition-colors p-5 flex items-start gap-4 disabled:opacity-50"
          >
            <div className="w-10 h-10 rounded-full bg-[#26282C] flex items-center justify-center flex-shrink-0">
              <X size={20} className="text-[#A8ADA5]" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#6D716C] mb-1">
                Cancel anyway
              </div>
              <div className="font-medium text-[#F5F3EE] mb-1">
                {cancelling || busy === "cancel" ? "Cancelling…" : "I still want to cancel"}
              </div>
              <div className="text-sm text-[#A8ADA5]">
                Access continues until the end of your paid period, then your plan drops to free.
                Cancel anytime again if you change your mind.
              </div>
            </div>
            <ArrowRight size={16} className="text-[#A8ADA5] flex-shrink-0 mt-2" />
          </button>
        </div>

        <Button
          onClick={() => onOpenChange(false)}
          disabled={busy !== null}
          className="mt-2 rounded-full btn-outline w-full"
          data-testid="save-offer-nevermind"
        >
          Never mind — keep my plan as-is
        </Button>
      </AlertDialogContent>
    </AlertDialog>
  );
}
